import { Box, ButtonBase, Icon, styled, useMediaQuery } from "@mui/material";
import useSettings from "app/hooks/useSettings";
import React, { Fragment } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Paragraph, Span } from "../Typography";
import MatxVerticalNavExpansionPanel from "./MatxVerticalNavExpansionPanel";
import { useState } from "react";
import useNavigationItems from "app/hooks/app/useNavigationItems";
import useApplications from "app/hooks/app/useApplications";
import { useEffect } from "react";

const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
  fontSize: "12px",
  marginTop: "20px",
  marginBottom: "10px",
  marginLeft: "12px",
  textTransform: "uppercase",
  display: mode === "compact" && "none",
  color: theme.palette.text.secondary,
}));

const ExtAndIntCommon = {
  display: "flex",
  overflow: "hidden",
  borderRadius: "4px",
  height: 44,
  whiteSpace: "pre",
  marginBottom: "8px",
  textDecoration: "none",
  justifyContent: "space-between",
  transition: "all 150ms ease-in",
  "&:hover": { background: "rgba(255, 255, 255, 0.08)" },
  "&.compactNavItem": {
    overflow: "hidden",
    justifyContent: "center !important",
  },
  "& .icon": {
    fontSize: "18px",
    paddingRight: "16px",
    verticalAlign: "middle",
  },
};
const ExternalLink = styled("a")(({ theme }) => ({
  ...ExtAndIntCommon,
  color: theme.palette.text.primary,
}));

const InternalLink = styled(Box)(({ theme }) => ({
  "& a": { ...ExtAndIntCommon, color: theme.palette.text.primary },
  "& .navItemActive": { backgroundColor: "rgba(255, 255, 255, 0.16)" },
}));

const StyledText = styled(Span)(({ mode }) => ({
  fontSize: "0.875rem",
  display: mode === "compact" && "none",
}));

const BulletIcon = styled("div")(({ theme }) => ({
  padding: "2px",
  marginLeft: "6px",
  marginRight: "20px",
  overflow: "hidden",
  borderRadius: "300px",
  background: theme.palette.text.primary,
}));

const BadgeValue = styled("div")({
  padding: "1px 8px",
  overflow: "hidden",
  borderRadius: "300px",
});

function findNavigationItemByPath(items, path) {
  if (!items || items.length === 0) return null;
  for (const item of items) {
    if (item.path === path) {
      return item; // Return the matching item
    }
    if (item.children) {
      // Recursively search in children
      const foundChild = findNavigationItemByPath(item.children, path);
      if (foundChild) return foundChild;
    }
  }
  return null; // No match found
}

const MatxVerticalNav = ({ items, onCloseSidebar }) => {
  const { settings } = useSettings();
  const { mode } = settings.layout1Settings.leftSidebar;
  const downLg = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const [activeApp, setActiveApp] = useState(undefined);
  const { navigationItems, activeNavigationItem, setActiveNavigationItem } =
    useNavigationItems();

  const location = useLocation();
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const page = params.get("path"); // Get the `path` query parameter

      const clearedPath = location.pathname + (page ? `?path=${page}` : "");

      // Use the recursive function to find the matching navigation item
      const existingItem = findNavigationItemByPath(navigationItems, clearedPath);

      if (existingItem) {
        setActiveNavigationItem(existingItem); // Set the active item
      }
    } catch (error) {
      console.error("An error occurred while updating the active navigation item:", error);
    }
  }, [location, navigationItems, setActiveNavigationItem]);


  const { activeApplicationId, setActiveApplicationId } = useApplications();

  const getTargetApp = (item) => {
    let targetUrl = "";
    if (item.path[0] === "/") targetUrl = item.path;
    else targetUrl = "/" + item.path;
    const pathArray = targetUrl.split("/");
    if (pathArray[1] === "app") return pathArray[2].split("?")[0];
    if (pathArray[1] === "account-app") return pathArray[2].split("?")[0];
    return undefined;
  };
  const getTargetAppPath = (item) => {
    let targetUrl = "";
    if (item.path[0] === "/") targetUrl = item.path;
    else targetUrl = "/" + item.path;
    const pathArray = targetUrl.split("?");
    if (pathArray.length > 1) {
      var paramsArray = pathArray[1].split("=");
      return paramsArray[1];
    }
    return undefined;
  };

  const getCurrentApp = () => {
    const pathArray = window.location.pathname.split("/");
    if (pathArray[1] === "app") return pathArray[2];
    if (pathArray[1] === "account-app") return pathArray[2];
    return undefined;
  };

  const currentApp = getCurrentApp();
  const renderLevels = (data, level) => {
    if (!data) return <></>;
    return data.map((item, index) => {
      if (item.type === "label") {
        return (
          <ListLabel key={index} mode={mode} className="sidenavHoverShow">
            {item.label}
          </ListLabel>
        );
      }

      if (item.children && item.children.length > 0) {
        return (
          <MatxVerticalNavExpansionPanel
            mode={mode}
            item={item}
            key={index}
            level={level}
          >
            {renderLevels(item.children, level + 1)}
          </MatxVerticalNavExpansionPanel>
        );
      }

      const targetApp = getTargetApp(item);
      const isShallow = targetApp && targetApp === currentApp;
      const targetAppPath = getTargetAppPath(item);

      if (item.type === "extLink") {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            target="_blank"
            rel="noopener noreferrer"
            className={`${mode === "compact" && "compactNavItem"}`}
          >
            <ButtonBase
              key={item.name}
              name="child"
              sx={{ width: "100%" }}
              style={{ paddingLeft: level * 8 + "px" }}
            >
              {(() => {
                if (item.icon) {
                  return <Icon className="icon" sx={{ width: 30 }}>{item.icon}</Icon>;
                } else {
                  return (
                    <span className="item-icon icon-text">{item.iconText}</span>
                  );
                }
              })()}
              {!item?.icon && (
                <BulletIcon
                  className="nav-bullet"
                  sx={{ display: mode === "compact" && "none" }}
                />
              )}

              <StyledText mode={mode} className="sidenavHoverShow">
                {item.name}
              </StyledText>

              <Box mx="auto" />

              {item.badge && <BadgeValue>{item.badge.value}</BadgeValue>}
            </ButtonBase>
          </ExternalLink>
        );
      } else {
        return (
          <InternalLink key={index}>
            <NavLink
              onClick={(e) => {
                setActiveNavigationItem(item);

                if (targetApp) setActiveApp(targetApp);
                else setActiveApp(undefined);

                if (isShallow) {
                  e.preventDefault();
                  document
                    .getElementById("app-iframe")
                    .contentWindow.postMessage({
                      type: "navigate",
                      payload: {
                        path: targetAppPath,
                      }
                    },
                      "*"
                    );
                  window.history.pushState(undefined, undefined, item.path);
                } else {
                  setActiveApplicationId(targetApp);
                }
              }}
              to={item.path}
              {...(downLg && { onClick: onCloseSidebar })}
              className={() =>
                activeNavigationItem && activeNavigationItem.path === item.path
                  ? `navItemActive ${mode === "compact" && "compactNavItem"}`
                  : `${mode === "compact" && "compactNavItem"}`
              }
            >
              <ButtonBase
                key={item.name}
                name="child"
                sx={{ width: "100%" }}
                style={{ paddingLeft: level * 8 + "px" }}
              >
                {item?.icon && (
                  <Icon className="icon" sx={{ width: 30 }}>
                    {item.icon}
                  </Icon>
                )}
                {!item?.icon && (
                  <BulletIcon
                    className="nav-bullet"
                    sx={{ display: mode === "compact" && "none" }}
                  />
                )}

                <StyledText mode={mode} className="sidenavHoverShow">
                  {item.name}
                </StyledText>

                <Box mx="auto" />

                {item.badge && (
                  <BadgeValue className="sidenavHoverShow">
                    {item.badge.value}
                  </BadgeValue>
                )}
              </ButtonBase>
            </NavLink>
          </InternalLink>
        );
      }
    });
  };

  return <div className="navigation">
    {renderLevels(navigationItems, 1)}
  </div>;
};

export default React.memo(MatxVerticalNav);
